import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Popover, Checkbox, Input, Tooltip, message } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { useTablePagination } from './useTablePagination';
import ActionButtons from './ActionButtons';
import type { PermAction } from '../../utils/permissions';
import ResizableTitle from './ResizableTitle';
import ExcelExportButton from './DownloadAsExcelFile';
import './table.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';

type ExportableColumn<T> = ColumnType<T> & { exportValue?: (row: T) => any };

const DEFAULT_COL_WIDTH = 150;
const DEFAULT_ID_COL_WIDTH = 72;
const DEFAULT_ACTION_COL_WIDTH = 104; // ~3 small icon buttons + gaps
const DEFAULT_TABLE_STYLE: React.CSSProperties = { padding: '2px' };

const AUTOFIT_SAMPLE_SIZE = 120;
const AUTOFIT_PADDING_PX = 24;
const AUTOFIT_MIN_PX = 60;
const AUTOFIT_MAX_PX = 520;

const normalizeWidth = (width: unknown): number | undefined => {
  if (typeof width === 'number' && Number.isFinite(width)) return width;
  if (typeof width === 'string') {
    // AntD allows string widths; we only support px-like numeric values for resizing.
    const match = width.trim().match(/^(\d+)(px)?$/i);
    if (match) return Number(match[1]);
  }
  return undefined;
};

const getColumnKey = (col: any, index: number): string => {
  const raw = col?.key ?? col?.dataIndex ?? `col-${index}`;
  return String(raw);
};

// Kolon görünümü kullanıcıya özel saklanır (aynı tarayıcıda kullanıcı başına ayrı anahtar)
const viewStorageKey = (persistKey: string): string => {
  let user = 'anon';
  try {
    user = localStorage.getItem('user') || 'anon';
  } catch { /* localStorage kapalıysa anon */ }
  return `table_view:${user}:${persistKey}`;
};

// Sayfa yolundan otomatik görünüm anahtarı üretir; sayısal segmentler :id'ye
// çevrilir ki /show/1425 gibi yollar tek tabloya çözülsün. Aynı sayfada birden
// çok tablo exportFileName ile ayrışır; o da aynıysa çağıran açık persistKey vermeli.
const deriveAutoPersistKey = (exportFileName: string): string => {
  let path = 'unknown';
  try {
    path = window.location.pathname.replace(/\/\d+(?=\/|$)/g, '/:id');
  } catch { /* window yoksa */ }
  return `auto:${path}:${exportFileName}`;
};

const loadSavedHiddenKeys = (persistKey?: string): string[] | null => {
  if (!persistKey) return null;
  try {
    const raw = localStorage.getItem(viewStorageKey(persistKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.hidden) ? parsed.hidden.map(String) : null;
  } catch {
    return null;
  }
};

const sumLeafColumnWidths = (cols: any[]): number => {
  return (cols || []).reduce((sum, col) => {
    if (Array.isArray(col?.children) && col.children.length > 0) {
      return sum + sumLeafColumnWidths(col.children);
    }
    return sum + (normalizeWidth(col?.width) ?? DEFAULT_COL_WIDTH);
  }, 0);
};

const inferDefaultWidth = (col: any, index: number): number => {
  const key = getColumnKey(col, index).toLowerCase();
  const dataIndex = col?.dataIndex;
  const title = typeof col?.title === 'string' ? col.title.toLowerCase() : '';

  const isIdCol =
    key === 'id' ||
    dataIndex === 'id' ||
    title === 'id' ||
    key.endsWith('_id') ||
    key.endsWith('.id');

  if (isIdCol) return DEFAULT_ID_COL_WIDTH;

  const isActionCol =
    /(^|[^a-z])(action|actions|operation|operations|işlem|islem)($|[^a-z])/i.test(key) ||
    /(^|[^a-z])(action|actions|operation|operations|işlem|islem)($|[^a-z])/i.test(title);

  if (isActionCol) return DEFAULT_ACTION_COL_WIDTH;

  return DEFAULT_COL_WIDTH;
};

interface StandardTableProps<T = any> {
  // Accept any[] to avoid strict generic incompatibilities across callers; we still
  // support optional exportValue at runtime when building export rows.
  columns: any[];
  /** Where to place the action column (key/dataIndex === 'action') if present. */
  actionColumnPosition?: 'first' | 'last';
  dataSource: T[];
  rowKey: string | ((record: T) => string);
  loading?: boolean;
  pagination?: false | object;
  className?: string;
  style?: React.CSSProperties;
  rowClassName?: (record: T, index: number) => string;
  scroll?: object;
  sticky?: boolean | { offsetHeader?: number; offsetScroll?: number; getContainer?: () => HTMLElement };
  size?: 'small' | 'middle' | 'large';
  bordered?: boolean;
  children?: React.ReactNode;
  onChange?: (pagination: any, filters: any, sorter: any, extra: any) => void;
  enableExport?: boolean;
  exportData?: any[];
  exportColumns?: { key: string; title?: string }[];
  /** Base name (without extension) for exported .xlsx/.csv files. */
  exportFileName?: string;
  enableCellTooltips?: boolean;
  enableColumnTools?: boolean;
  enableResetView?: boolean;
  /** Başlangıçta gizli gelecek kolon key'leri (kullanıcının kayıtlı görünümü varsa o kazanır) */
  defaultHiddenColumnKeys?: string[];
  /**
   * Kolon görünümünün kullanıcıya özel saklanacağı anahtar (localStorage).
   * Verilmezse sayfa yolu + exportFileName'den otomatik üretilir (tüm tablolarda
   * "Görünümü kaydet" aktif olur); false ile tamamen kapatılır.
   */
  persistKey?: string | false;
  rowSelection?: TableProps<T>['rowSelection'];
  expandable?: TableProps<T>['expandable'];
  locale?: TableProps<T>['locale'];
  footerLeftExtra?: React.ReactNode;
  onRow?: TableProps<T>['onRow'];
  components?: TableProps<T>['components'];
}

interface ResizableColumnType extends ColumnType<any> {
  width?: number;
}

const defaultRowClassName = (_: any, index: number) =>
  index % 2 === 0 ? 'zebra-row' : '';

// Extend ColumnType with optional exportValue accessor
// (kept above and used throughout)

function StandardTable<T = any>({
  columns,
  actionColumnPosition = 'last',
  dataSource,
  rowKey,
  loading = false,
  pagination,
  className = 'compact-table zebra-table left-align-table single-line-table',
  style = DEFAULT_TABLE_STYLE,
  rowClassName = defaultRowClassName,
  scroll,
  sticky,
  size = 'small',
  bordered = false,
  children,
  onChange,
  enableExport = true,
  exportData,
  exportColumns,
  exportFileName = 'table_data',
  enableCellTooltips = false,
  enableColumnTools = true,
  enableResetView = true,
  defaultHiddenColumnKeys,
  persistKey,
  rowSelection,
  expandable,
  locale,
  footerLeftExtra,
  onRow,
  components,
}: StandardTableProps<T>) {
  const { t } = useTranslation();

  const orderedColumns = useMemo(() => {
    const cols = Array.isArray(columns) ? columns : [];
    if (actionColumnPosition !== 'first') return cols;

    const actionIndex = cols.findIndex((c, i) => getColumnKey(c as any, i).toLowerCase() === 'action');
    if (actionIndex <= 0) return cols;

    const next = [...cols];
    const [actionCol] = next.splice(actionIndex, 1);
    next.unshift(actionCol);
    return next;
  }, [columns, actionColumnPosition]);

  // Açık anahtar > otomatik (sayfa yolu + dosya adı) > false ile kapalı
  const effectivePersistKey =
    persistKey === false ? undefined : (persistKey ?? deriveAutoPersistKey(exportFileName));
  const shouldPaginate = pagination !== false;
  const { currentPage, pageSize, setCurrentPage, getPaginationProps } = useTablePagination(
    typeof pagination === 'object' && pagination && 'pageSize' in pagination && typeof (pagination as any).pageSize === 'number'
      ? (pagination as any).pageSize as number
      : 20 // Default page size
  );
  const effectivePagination = useMemo(() => {
    if (!shouldPaginate) return false;

    const base = (typeof pagination === 'object' && pagination) ? (pagination as any) : {};
    // Exclude controlled fields + handlers to avoid churn and double-binding.
    const {
      current: _current,
      pageSize: _pageSize,
      total: _total,
      onChange: _onChange,
      onShowSizeChange: _onShowSizeChange,
      ...rest
    } = base;

    return {
      ...rest,
      ...getPaginationProps(dataSource.length),
    };
    // Intentionally do NOT depend on the full `pagination` object reference;
    // many callers pass inline literals which would cause unnecessary re-computation.
  }, [
    shouldPaginate,
    dataSource.length,
    getPaginationProps,
    typeof pagination === 'object' && pagination ? (pagination as any).showSizeChanger : undefined,
    typeof pagination === 'object' && pagination ? (pagination as any).hideOnSinglePage : undefined,
    typeof pagination === 'object' && pagination ? (pagination as any).simple : undefined,
    typeof pagination === 'object' && pagination ? (pagination as any).size : undefined,
    typeof pagination === 'object' && pagination ? (pagination as any).position : undefined,
    typeof pagination === 'object' && pagination ? (pagination as any).pageSizeOptions?.join?.(',') : undefined,
    typeof pagination === 'object' && pagination ? (pagination as any).showTotal : undefined,
  ]);

  // Reset to first page when data shrinks so current page is out of range
  useEffect(() => {
    const total = dataSource.length;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(1);
    }
  }, [dataSource.length, pageSize]);

  const [columnsState, setColumnsState] = useState<(ResizableColumnType & { exportValue?: (row: T) => any })[]>(
    (orderedColumns || []).map((col: any, i: number) => ({ ...(col as any), width: normalizeWidth((col as any).width) ?? inferDefaultWidth(col, i) }))
  );

  // Track hidden columns by key: kayıtlı kullanıcı görünümü > çağıranın varsayılanı > boş
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(
    () => new Set(loadSavedHiddenKeys(effectivePersistKey) ?? defaultHiddenColumnKeys ?? [])
  );

  // Keep columns state in sync when parent updates columns.
  // Preserve user-resized widths, but always pick up latest render/sorter/title.
  // Comparing only keys+widths left first-mount closures frozen (e.g. empty warehouse maps).
  useEffect(() => {
    // IMPORTANT: Many callers create `columns` inline (new array every render).
    // If we fully reset state on reference changes, widths will "jump" / look inconsistent.
    // Instead, merge: prefer explicit caller width, else keep previous width for same key.
    setColumnsState((prev) => {
      const prevByKey = new Map<string, any>();
      (prev || []).forEach((c, i) => {
        prevByKey.set(getColumnKey(c as any, i), c);
      });

      const next = (orderedColumns || []).map((col: any, i: number) => {
        const key = getColumnKey(col as any, i);
        const prevCol = prevByKey.get(key);
        const width =
          normalizeWidth(col?.width) ??
          normalizeWidth(prevCol?.width) ??
          inferDefaultWidth(col, i);
        return { ...(col as any), width };
      });

      const unchanged =
        Array.isArray(prev) &&
        prev.length === next.length &&
        prev.every((p: any, idx: number) => {
          const n: any = next[idx];
          return (
            getColumnKey(p, idx) === getColumnKey(n, idx) &&
            normalizeWidth((p as any).width) === normalizeWidth((n as any).width) &&
            p.render === n.render &&
            p.sorter === n.sorter &&
            p.title === n.title &&
            p.exportValue === n.exportValue &&
            p.filters === n.filters &&
            p.onFilter === n.onFilter &&
            p.filteredValue === n.filteredValue
          );
        });
      return unchanged ? prev : next;
    });
    // Sanitize hidden keys when columns change (remove keys that no longer exist)
    const currentKeys = new Set((orderedColumns || []).map((c, i) => getColumnKey(c as any, i)));
    setHiddenKeys(prev => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set<string>();
      prev.forEach(k => {
        if (currentKeys.has(k)) next.add(k);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [orderedColumns]);

  const handleResize = useCallback((index: number) => {
    return (_e: React.SyntheticEvent<Element>, { size }: { size: { width: number } }) => {
      setColumnsState((prev) => {
        const nextColumns = [...prev];
        nextColumns[index] = { ...nextColumns[index], width: size.width };
        return nextColumns;
      });
    };
  }, []);

  const mergedColumns = useMemo(() => {
    return columnsState.map((col, i) => {
      const prevOnHeaderCell = (col as any).onHeaderCell as (c: ColumnType<T>) => any;
      return {
        ...(col as any),
        onHeaderCell: (column: ColumnType<T>) => ({
          ...(typeof prevOnHeaderCell === 'function' ? prevOnHeaderCell(column) : {}),
          width: column.width,
          style: { width: column.width, minWidth: 0 },
          minWidth: 0,
          maxWidth: undefined,
          onResize: handleResize(i),
        }),
      } as any;
    });
  }, [columnsState, handleResize]);

  // Apply visibility filtering
  const visibleColumns = useMemo(() => {
    return mergedColumns.filter((col, i) => !hiddenKeys.has(getColumnKey(col, i)));
  }, [mergedColumns, hiddenKeys]);

  // Flatten only leaf columns for auto-fit (skip groups)
  const visibleLeafColumnsForAutofit = useMemo(() => {
    const walk = (cols: any[], out: any[]) => {
      (cols || []).forEach((c: any) => {
        if (Array.isArray(c?.children) && c.children.length > 0) {
          walk(c.children, out);
        } else {
          out.push(c);
        }
      });
    };
    const out: any[] = [];
    walk(visibleColumns as any, out);
    return out;
  }, [visibleColumns]);

  const handleAutofitWidths = useCallback(() => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const getValueByPathSafe = (obj: any, path: any) => {
        if (obj == null || path == null) return undefined;
        if (Array.isArray(path) && path.length > 0) {
          try {
            return path.reduce((acc: any, key: any) => (acc == null ? undefined : acc[key]), obj);
          } catch {
            return undefined;
          }
        }
        return obj[path];
      };

      // Best-effort font match; fallback to app defaults.
      // (DOM query is only on click, so it’s acceptable.)
      const tableEl = document.querySelector('.ant-table');
      const font = tableEl ? window.getComputedStyle(tableEl).font : '';
      ctx.font = font && font !== 'normal' ? font : '12px Arial';

      const sample = Array.isArray(dataSource)
        ? dataSource.slice(0, AUTOFIT_SAMPLE_SIZE)
        : [];

      const nextWidthByKey = new Map<string, number>();

      visibleLeafColumnsForAutofit.forEach((col: any, idx: number) => {
        const key = getColumnKey(col, idx);
        const explicitMin = normalizeWidth(col?.minWidth);
        const explicitMax = normalizeWidth(col?.maxWidth);
        const baseMin = Math.max(AUTOFIT_MIN_PX, explicitMin ?? 0);
        const baseMax = Math.min(AUTOFIT_MAX_PX, explicitMax ?? AUTOFIT_MAX_PX);

        // header width
        const headerText = typeof col?.title === 'string' ? col.title : '';
        let maxTextPx = ctx.measureText(String(headerText)).width;

        // sample cell widths
        const di = col?.dataIndex;
        const exportValueFn = (col as any).exportValue as ((row: any) => any) | undefined;
        const renderFn = (col as any).render as ((value: any, record: any, index: number) => any) | undefined;

        for (let rIdx = 0; rIdx < sample.length; rIdx++) {
          const row = sample[rIdx];
          let raw: any = undefined;

          if (typeof exportValueFn === 'function') {
            try {
              raw = exportValueFn(row);
            } catch {
              raw = undefined;
            }
          } else {
            const valueFromDataIndex = di != null ? getValueByPathSafe(row, di) : undefined;

            if (typeof renderFn === 'function') {
              // Prefer what the user actually sees (render output), but only if primitive.
              try {
                const rendered = renderFn(valueFromDataIndex, row, rIdx);
                raw = rendered;
              } catch {
                raw = undefined;
              }
            }

            if (raw == null || typeof raw === 'object') {
              raw = valueFromDataIndex;
            }
          }

          if (raw == null) continue;
          // Avoid huge JSON blobs / ReactNodes
          const s =
            typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean'
              ? String(raw)
              : '';
          if (!s) continue;
          const w = ctx.measureText(s).width;
          if (w > maxTextPx) maxTextPx = w;
        }

        const target = Math.ceil(maxTextPx + AUTOFIT_PADDING_PX);
        const clamped = Math.max(baseMin, Math.min(baseMax, target));
        nextWidthByKey.set(key, clamped);
      });

      setColumnsState((prev) => {
        return (prev || []).map((c: any, i: number) => {
          const key = getColumnKey(c, i);
          const nextW = nextWidthByKey.get(key);
          if (!nextW) return c;
          const prevW = normalizeWidth(c?.width);
          if (prevW === nextW) return c;
          return { ...c, width: nextW };
        });
      });
    } catch {
      // no-op
    }
  }, [dataSource, visibleLeafColumnsForAutofit]);

  // rc-table's triggerOnScroll can infinite-loop when scroll.x is 'max-content'
  // together with tableLayout="fixed": pingedLeft/pingedRight shadows change
  // the measured width, which retriggers the effect. A numeric x (sum of leaf
  // column widths) is the actual layout width and keeps the object stable.
  const computedScrollX = useMemo(
    () => Math.max(sumLeafColumnWidths(visibleColumns), 1),
    [visibleColumns]
  );
  const scrollX = (scroll as { x?: number | string | true; y?: number | string } | undefined)?.x;
  const scrollY = (scroll as { x?: number | string | true; y?: number | string } | undefined)?.y;
  const effectiveScroll = useMemo(() => {
    const useComputed = scrollX === 'max-content' || scrollX === true || scrollX == null;
    const next: { x: number | string; y?: number | string } = {
      x: useComputed ? computedScrollX : scrollX,
    };
    if (scrollY != null) next.y = scrollY;
    return next;
  }, [scrollX, scrollY, computedScrollX]);

  const handleTableChange = useCallback((pag: any, filters: any, sorter: any, extra: any) => {
    // Pagination state updates are handled by AntD invoking
    // pagination.onChange / onShowSizeChange directly (see useTablePagination);
    // calling them again here would double-fire the handlers.
    if (onChange) onChange(pag, filters, sorter, extra);
  }, [onChange]);

  // Tooltip logic for columns with ellipsis and no render
  // const renderWithTooltip = (text: any) => {
  //   if (text === null || text === undefined) return '';
  //   const str = String(text);
  //   const shortText = str.length > 24 ? str.slice(0, 24) + '…' : str;
  //   return (
  //     <Tooltip title={str}>
  //       <span style={{ display: 'inline-block', width: '100%' }}>{shortText}</span>
  //     </Tooltip>
  //   );
  // };

  // Enhance columns recursively so grouped children honor width & styles
  const enhancedColumns = useMemo(() => {
    const collator = new Intl.Collator('tr', { sensitivity: 'base', numeric: true });
    const getValueByPath = (row: any, di: any) => {
      if (Array.isArray(di) && di.length > 0) {
        try { return di.reduce((acc: any, key: any) => (acc == null ? undefined : acc[key]), row); } catch { return undefined; }
      }
      return di != null ? row?.[di] : undefined;
    };
    const toComparable = (val: any) => {
      if (val == null) return null;
      if (typeof val === 'number') return val;
      if (val instanceof Date) return val.getTime();
      if (typeof val === 'string') {
        // Only treat ISO-like strings as dates; Date.parse alone is too loose
        // (e.g. '2024' or 'May 5' would otherwise be parsed as dates and mis-sort text columns).
        if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
          const ts = Date.parse(val);
          if (!Number.isNaN(ts)) return ts;
        }
        return val;
      }
      if (typeof val === 'boolean') return val ? 1 : 0;
      return String(val);
    };

    const enhance = (col: any): any => {
      const width = (col as any).width ?? 150;
      const dataIndex = (col as any).dataIndex as any;
      const hasCustomSorter = typeof (col as any).sorter !== 'undefined';
      const origOnHeaderCell = (col as any).onHeaderCell;
      const origOnCell = (col as any).onCell;
      const align = (col as any).align as ('left' | 'center' | 'right' | undefined);
      const origRender = (col as any).render as ((value: any, record: any, index: number) => React.ReactNode) | undefined;
      // rc-table no longer adds ant-table-column-align-*; table.css left-align
      // rules use !important, so we stamp a class the CSS can target.
      const alignClass = align && align !== 'left' ? `std-table-align-${align}` : undefined;
      const mergeAlignClass = (existing?: string) =>
        [existing, alignClass].filter(Boolean).join(' ') || undefined;

      const next: any = {
        ...col,
        width,
        ellipsis: (col as any).ellipsis ?? true,
        className: mergeAlignClass((col as any).className),
        onHeaderCell: (column: ColumnType<T>) => {
          const base = (typeof origOnHeaderCell === 'function' ? origOnHeaderCell(column) : {}) as any;
          const baseStyle = (base && typeof base === 'object') ? (base.style || {}) : {};
          const minWidth = baseStyle.minWidth ?? 0;
          const maxWidth = baseStyle.maxWidth;
          return {
            ...base,
            className: mergeAlignClass(base.className),
            style: {
              ...baseStyle,
              width,
              minWidth,
              ...(maxWidth !== undefined ? { maxWidth } : {}),
              ...(align ? { textAlign: align } : {}),
            },
          };
        },
        onCell: (record: any, rowIndex: number) => {
          const base = (typeof origOnCell === 'function' ? origOnCell(record, rowIndex) : {}) as any;
          const baseStyle = (base && typeof base === 'object') ? (base.style || {}) : {};
          const minWidth = baseStyle.minWidth ?? 0;
          const maxWidth = baseStyle.maxWidth ?? width;
          return {
            ...base,
            className: mergeAlignClass(base.className),
            // Cheap native tooltip so truncated (ellipsis) content stays readable
            // even when the heavier antd Tooltip mode is disabled.
            ...(!enableCellTooltips && typeof dataIndex !== 'undefined'
              ? (() => {
                  const raw = getValueByPath(record, dataIndex);
                  return raw == null || raw === '' || typeof raw === 'object' ? {} : { title: String(raw) };
                })()
              : {}),
            style: {
              ...baseStyle,
              width,
              maxWidth,
              minWidth,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              ...(align ? { textAlign: align } : {}),
            },
          };
        },
      };

      if (enableCellTooltips) {
        const tooltipValueFn = (col as any).tooltipValue as ((row: any) => any) | undefined;
        const exportValueFn = (col as any).exportValue as ((row: any) => any) | undefined;

        next.render = (value: any, record: any, index: number) => {
          const content = typeof origRender === 'function' ? origRender(value, record, index) : (value == null ? '' : String(value));

          const tooltipRaw =
            (typeof tooltipValueFn === 'function'
              ? tooltipValueFn(record)
              : typeof exportValueFn === 'function'
                ? exportValueFn(record)
                : typeof dataIndex !== 'undefined'
                  ? getValueByPath(record, dataIndex)
                  : value);

          if (tooltipRaw == null || tooltipRaw === '') return content;
          const tooltipText = String(tooltipRaw);

          return (
            <Tooltip title={tooltipText} placement="topLeft">
              <span style={{ display: 'inline-block', width: '100%' }}>{content}</span>
            </Tooltip>
          );
        };
      }

      if (!hasCustomSorter && (typeof dataIndex !== 'undefined')) {
        next.sorter = (a: any, b: any) => {
          const avRaw = getValueByPath(a, dataIndex);
          const bvRaw = getValueByPath(b, dataIndex);
          const av = toComparable(avRaw);
          const bv = toComparable(bvRaw);
          if (av == null && bv == null) return 0;
          if (av == null) return -1;
          if (bv == null) return 1;
          if (typeof av === 'number' && typeof bv === 'number') return av - bv;
          if (typeof av === 'string' && typeof bv === 'string') return collator.compare(av, bv);
          return collator.compare(String(av), String(bv));
        };
        next.sortDirections = (col as any).sortDirections ?? ['ascend', 'descend'];
        next.showSorterTooltip = (col as any).showSorterTooltip ?? false;
      }

      if (Array.isArray(col.children) && col.children.length > 0) {
        next.children = col.children.map((child: any) => enhance(child));
      }

      return next;
    };

    return visibleColumns.map((c: any) => enhance(c));
  }, [visibleColumns]);

  // Popover content for column visibility toggling
  const [columnSearch, setColumnSearch] = useState('');
  const allColumnKeys = useMemo(() => (columnsState || []).map((col, i) => getColumnKey(col, i)), [columnsState]);
  const hideAllColumns = () => {
    // Avoid hiding everything; keep at least one column visible to prevent a broken table layout.
    const keys = [...allColumnKeys];
    if (keys.length <= 1) return;
    setHiddenKeys(new Set(keys.slice(1)));
  };
  const showAllColumns = () => setHiddenKeys(new Set());

  const handleSaveView = () => {
    if (!effectivePersistKey) return;
    try {
      localStorage.setItem(viewStorageKey(effectivePersistKey), JSON.stringify({ hidden: Array.from(hiddenKeys) }));
      message.success(t('common.viewSaved'));
    } catch {
      message.error(t('common.viewSaveFailed'));
    }
  };

  const columnsToggleContent = useMemo(() => {
    const query = columnSearch.trim().toLowerCase();

    const items = (columnsState || [])
      .map((col, i) => {
        const key = getColumnKey(col, i);
        const titleNode = (col as any).title;
        const title = typeof titleNode === 'string' ? titleNode : (titleNode ? t('common.column') : `${t('common.column')} ${i + 1}`);
        return { col, i, key, title };
      })
      .filter((item) => (query ? item.title.toLowerCase().includes(query) : true));

    const visibleCount = allColumnKeys.filter((k) => !hiddenKeys.has(k)).length;
    const canHideMore = visibleCount > 1;

    return (
      <div style={{ width: 260 }}>
        <Input
          size="small"
          placeholder={t('common.searchColumns')}
          allowClear
          value={columnSearch}
          onChange={(e) => setColumnSearch(e.target.value)}
          aria-label={t('common.searchColumns')}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 8 }}>
          <Button size="small" onClick={showAllColumns} disabled={hiddenKeys.size === 0} aria-label={t('common.showAll')}>
            {t('common.showAll')}
          </Button>
          <Button size="small" onClick={hideAllColumns} disabled={!canHideMore} aria-label={t('common.hideAll')}>
            {t('common.hideAll')}
          </Button>
          {effectivePersistKey && (
            <Button size="small" type="primary" onClick={handleSaveView} aria-label={t('common.saveView')}>
              {t('common.saveView')}
            </Button>
          )}
        </div>
        <div style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 4 }}>
          {items.map(({ key, title }) => {
            const checked = !hiddenKeys.has(key);
            return (
              <div key={key} style={{ marginBottom: 6 }}>
                <Checkbox
                  checked={checked}
                  onChange={(e) => {
                    setHiddenKeys((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.delete(key);
                      else next.add(key);
                      return next;
                    });
                  }}
                >
                  {title}
                </Checkbox>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [allColumnKeys, columnSearch, columnsState, hiddenKeys, effectivePersistKey, t]);

  const handleResetView = () => {
    setHiddenKeys(new Set(defaultHiddenColumnKeys ?? []));
    if (effectivePersistKey) {
      try { localStorage.removeItem(viewStorageKey(effectivePersistKey)); } catch { /* yoksay */ }
    }
    // Also reset widths to defaults
    setColumnsState((orderedColumns || []).map((col: any, i: number) => ({ ...col, width: normalizeWidth(col.width) ?? inferDefaultWidth(col, i) })));
  };


  // Build export rows using column accessors
  const buildCellValue = (col: ExportableColumn<T>, row: any) => {
    // Priority: explicit exportValue -> dataIndex path -> fallback ''
    if (typeof col.exportValue === 'function') {
      try { return col.exportValue(row); } catch { return ''; }
    }
    const di = (col as any).dataIndex as any;
    if (Array.isArray(di) && di.length > 0) {
      try { return di.reduce((acc: any, key: any) => (acc == null ? undefined : acc[key]), row); } catch { return ''; }
    } else if (di != null) {
      return row[di];
    }
    return '';
  };

  // Flatten only leaf columns (skip header groups) and compose a readable title path
  const flattenLeafColumns = (cols: any[], parentTitles: string[] = []): { col: any; title: string }[] => {
    const out: { col: any; title: string }[] = [];
    for (const c of cols || []) {
      const tNode = (c as any).title;
      const titleStr = typeof tNode === 'string' ? tNode : undefined;
      const path = titleStr ? [...parentTitles, titleStr] : parentTitles;
      if (Array.isArray((c as any).children) && (c as any).children.length > 0) {
        out.push(...flattenLeafColumns((c as any).children, path));
      } else {
        const finalTitle = path.length > 0 ? path.join(' / ') : t('common.col');
        out.push({ col: c, title: finalTitle });
      }
    }
    return out;
  };

  // Use visible (non-hidden) columns for export and only include leaves
  const leafColumns = useMemo(() => flattenLeafColumns(visibleColumns), [visibleColumns]);

  const defaultExportColumns = useMemo(() => {
    return leafColumns.map((item, i) => ({
      key: String((item.col as any).dataIndex ?? (item.col as any).key ?? `col-${i}`),
      title: item.title || `${t('common.col')} ${i + 1}`,
    }));
  }, [leafColumns, t]);

  const effectiveExportColumns = useMemo(() => {
    if (Array.isArray(exportColumns) && exportColumns.length > 0) return exportColumns;
    return defaultExportColumns;
  }, [defaultExportColumns, exportColumns]);

  // Built lazily (on export click) to avoid materializing a full copy of the
  // dataset on every render for tables the user never exports.
  const buildExportRows = () => {
    if (Array.isArray(exportData)) return exportData;
    return (dataSource || []).map((row: any) => {
      const obj: any = {};
      leafColumns.forEach((item, i) => {
        const title = item.title || `${t('common.col')} ${i + 1}`;
        obj[title] = buildCellValue(item.col as any, row);
      });
      return obj;
    });
  };

  // CSV export helpers
  const toCsvField = (val: any) => {
    if (val == null) return '';
    const s = String(val);
    if (/[",\n\r]/.test(s)) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const handleExportCsv = () => {
    const headers = Array.isArray(exportData)
      ? effectiveExportColumns.map((c) => String(c.title || c.key))
      : leafColumns.map((item, i) => item.title || `${t('common.col')} ${i + 1}`);
    const lines: string[] = [];
    lines.push(headers.map(toCsvField).join(','));
    for (const row of buildExportRows() as any[]) {
      const line = headers.map(h => toCsvField((row as any)[h]));
      lines.push(line.join(','));
    }
    const csv = lines.join('\r\n');
    // UTF-8 BOM so Excel opens Turkish characters correctly
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFileName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Merge caller-provided components with our header cell override for resizable columns
  const mergedComponents = useMemo(() => {
    const baseHeader = { cell: ResizableTitle } as any;
    const callerHeader = (components as any)?.header || {};
    return {
      ...(components as any),
      header: { ...callerHeader, ...baseHeader },
    } as TableProps<T>['components'];
  }, [components]);

  const shouldShowFooterControls = enableExport || enableColumnTools || enableResetView || Boolean(footerLeftExtra);

  return (
    <Table
      columns={enhancedColumns}
      dataSource={dataSource}
      rowKey={rowKey}
      loading={loading}
      pagination={effectivePagination}
      expandable={expandable}
      onRow={onRow as any}
      onChange={handleTableChange}
      className={className}
      style={style}
      rowClassName={rowClassName}
      scroll={effectiveScroll}
      size={size}
      bordered={bordered}
      rowSelection={rowSelection as any}
      locale={locale}
      tableLayout="fixed"
      components={mergedComponents}
      footer={
        shouldShowFooterControls
          ? () => (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {enableExport && (
                    <ExcelExportButton
                      data={buildExportRows}
                      filename={`${exportFileName}.xlsx`}
                      sheetName="Data"
                      size="small"
                      columns={Array.isArray(exportData) ? effectiveExportColumns : undefined}
                    />
                  )}
                  {enableExport && (
                    <Button size="small" onClick={handleExportCsv} aria-label={t('common.exportCsv')}>
                      {t('common.exportCsv')}
                    </Button>
                  )}
                  {enableColumnTools && (
                    <Popover placement="topLeft" content={columnsToggleContent} title={t('common.columns')} trigger="click">
                      <Button size="small" aria-label={t('common.columns')}>
                        {t('common.columns')}
                      </Button>
                    </Popover>
                  )}
                  {enableColumnTools && (
                    <Button size="small" onClick={handleAutofitWidths} aria-label="Auto-fit widths">
                      Auto-fit
                    </Button>
                  )}
                  {enableResetView && (
                    <Button size="small" onClick={handleResetView} aria-label={t('common.resetView')}>
                      {t('common.resetView')}
                    </Button>
                  )}
                  {footerLeftExtra}
                </div>
              </div>
            )
          : undefined
      }
    />
  );
}

export default StandardTable;

// Reusable action column factory for consistent action button styling
export interface ActionColumnOptions<T = any> {
  title?: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  size?: 'small' | 'middle' | 'large';
  gap?: number;
  onView?: (record: T) => void;
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  // Optional per-row extra renderer for additional custom actions
  extraRender?: (record: T) => React.ReactNode;
  showView?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  /**
   * Optional centralized permission gating for the action buttons.
   * This only affects UI visibility/disabled state; backend must still enforce authorization.
   */
  guardPermissions?: boolean;
  /** Permission check path override (defaults to current route path inside ActionButtons). */
  permissionPath?: string;
  /** When unauthorized: 'hide' removes button; 'disable' renders disabled button. */
  permissionMode?: 'hide' | 'disable';
  /** Action mapping overrides (defaults: view->read, edit->edit, delete->delete). */
  viewAction?: PermAction;
  editAction?: PermAction;
  deleteAction?: PermAction;
  className?: string;
}

export function createActionColumn<T = any>(options: ActionColumnOptions<T> = {}): ColumnType<T> {
  const {
    title = i18n.t('common.actions'),
    width,
    align = 'left',
    size = 'small',
    gap = 4,
    onView,
    onEdit,
    onDelete,
    extraRender,
    showView = true,
    showEdit = true,
    showDelete = true,
    guardPermissions = true,
    permissionPath,
    permissionMode = 'disable',
    viewAction,
    editAction,
    deleteAction,
    className = 'action-col',
  } = options;

  // Compute a tight width only if not explicitly provided
  const visibleButtonsCount = [showView, showEdit, showDelete].filter(Boolean).length;
  const perButtonWidth = size === 'large' ? 40 : size === 'middle' ? 32 : 28; // approximate icon-only button widths
  const computedWidth = visibleButtonsCount > 0
    ? (visibleButtonsCount * perButtonWidth) + (visibleButtonsCount - 1) * gap + 8
    : 0;

  return {
    title,
    key: 'action',
    width: typeof width === 'number' ? width : computedWidth,
    align: align as any,
    className,
    render: (_: any, record: T) => (
      <ActionButtons
        onView={onView ? () => onView(record) : undefined}
        onEdit={onEdit ? () => onEdit(record) : undefined}
        onDelete={onDelete ? () => onDelete(record) : undefined}
        size={size}
        gap={gap}
        showView={showView}
        showEdit={showEdit}
        showDelete={showDelete}
        guardPermissions={guardPermissions}
        permissionPath={permissionPath}
        permissionMode={permissionMode}
        viewAction={viewAction}
        editAction={editAction}
        deleteAction={deleteAction}
        extra={typeof extraRender === 'function' ? extraRender(record) : undefined}
      />
    ),
  } as any;
}